import { api } from "@/api"

import { useQuery, useQueries } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"
import type { FileExportFormat } from "@/composables/options/useFileExportFormatOptions"

export type ProductSalesReportDatapoint = DeepReadonly<{
  start: Date
  end: Date
  value: number
}>

export type ProductSalesReport = DeepReadonly<{
  dataset: ProductSalesReportDatapoint[]
  granularity: Granularity
}>

export type ProductSalesReportQuery = {
  categoryId?: string
  productTagId?: string
  deviceId?: number
  cashierId?: string
  start?: string
  end?: string
}

export const retrieveProductSalesReport = async (
  query: ProductSalesReportQuery
): Promise<ProductSalesReport> => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<ProductSalesReport>
  >("/reports/product-sales/", {
    query: query,
  })
  return {
    ...body,
    dataset: body.dataset.map((entry) => ({
      ...entry,
      start: DateFns.parseISO(entry.start),
      end: DateFns.parseISO(entry.end),
    })),
  }
}

export const useProductSalesReport = (
  query: MaybeRef<ProductSalesReportQuery>
) =>
  useQuery(
    [BASE_PRODUCT_SALES_REPORT_QUERY_KEY, query] as const,
    ({ queryKey: [_name, query_] }) =>
      retrieveProductSalesReport(unref(query_)),
    { refetchInterval: 60 * 1000 }
  )

export const useProductSalesReports = (
  queries: MaybeRef<MaybeRef<ProductSalesReportQuery>[]>
) => {
  const queryResults = useQueries({
    queries: computed(() =>
      unref(queries).map((query) => ({
        queryKey: [
          BASE_PRODUCT_SALES_REPORT_QUERY_KEY,
          unref(query),
        ] as const,
        queryFn: () =>
          retrieveProductSalesReport(unref(query)),
        refetchInterval: 60 * 1000,
      }))
    ),
  })

  const isLoading = computed(() =>
    queryResults.some((result) => result.isLoading)
  )

  const errors = computed(() =>
    queryResults.map((result) => result.error)
  )

  const refetch = async () => {
    if (queryResults.length === 0) {
      return []
    }
    const results = await Promise.all(
      queryResults.map((result) => {
        return result.refetch()
      })
    )
    return results
  }

  return {
    queryResults,
    isLoading,
    errors,
    refetch,
  }
}

export const exportProductSalesReport = async (
  query: ProductSalesReportQuery,
  format?: FileExportFormat
) => {
  const response = await api.get<Blob | string>(
    "/reports/product-sales/export/",
    {
      query: {
        ...query,
        format,
      },
    }
  )
  downloadFileFromResponse(response)
}
